import React from 'react';
import MainButton from '../../../components/agv-button';
import Status from '../../../components/agv-status';
import { STATUS_ENUM } from '../../../components/agv-status/status-enum';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import LogPayloadDiff from './LogPayloadDiff';
import {
  computeJsonDiff,
  ERROR_TYPE_LABELS,
  formatDateTime,
  HISTORY_EVENT_LABELS,
  STOP_REASON_LABELS,
} from '../logs.utils';

function getEventPresentation(event) {
  if (event.event_type === 'ATTEMPT') {
    return event.status === 'DONE'
      ? {
          dot: 'tw-bg-status-positive',
          badge: STATUS_ENUM.POSITIVE,
          badgeText: 'Succès',
        }
      : {
          dot: 'tw-bg-status-negative',
          badge: STATUS_ENUM.NEGATIVE,
          badgeText: 'Échec',
        };
  }
  if (event.event_type === 'MANUAL_RETRY') {
    return {
      dot: 'tw-bg-status-info',
      badge: STATUS_ENUM.INFO,
      badgeText: 'Demandé',
    };
  }
  return {
    dot: 'tw-bg-status-secondary',
    badge: STATUS_ENUM.SECONDARY,
    badgeText: 'Corrigé',
  };
}

function HistoryEvent({ event }) {
  const presentation = getEventPresentation(event);
  const author = event.user_full_name || event.username;
  const diff =
    event.event_type === 'MANUAL_EDIT'
      ? computeJsonDiff(event.datas_before, event.datas_after)
      : [];

  return (
    <li className="tw-relative tw-pl-6 tw-pb-4 tw-border-0 tw-border-l tw-border-solid tw-border-black/10 last:tw-border-transparent">
      <span
        className={`tw-absolute tw-left-[-5px] tw-top-1 tw-size-2.5 tw-rounded-full ${presentation.dot}`}
        aria-hidden="true"
      />
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-text-sm tw-font-medium">
          {HISTORY_EVENT_LABELS[event.event_type] || event.event_type}
        </span>
        <Status status={presentation.badge} text={presentation.badgeText} />
        {event.error?.type && (
          <span className="tw-text-xs tw-text-text-dark-medium-importance">
            {ERROR_TYPE_LABELS[event.error.type] || event.error.type}
          </span>
        )}
      </div>
      <div className="tw-text-xs tw-text-text-dark-medium-importance tw-mt-0.5">
        {formatDateTime(event.created_at)}
        {author ? ` · par ${author}` : ''}
      </div>
      {event.error?.message && (
        <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-status-negative tw-break-words">
          {event.error.message}
        </p>
      )}
      {STOP_REASON_LABELS[event.error?.stop_reason] && (
        <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-text-dark-medium-importance">
          Essais automatiques arrêtés :{' '}
          {STOP_REASON_LABELS[event.error.stop_reason]}
        </p>
      )}
      {event.event_type === 'ATTEMPT' &&
        event.status === 'DONE' &&
        event.response?.message && (
          <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-text-dark-medium-importance">
            Réponse de l’AS400 : {event.response.message}
          </p>
        )}
      {event.event_type === 'MANUAL_EDIT' && (
        <div className="tw-mt-2">
          <LogPayloadDiff diff={diff} />
        </div>
      )}
    </li>
  );
}

/**
 * Historique d'un envoi, du plus récent au plus ancien.
 */
export default function LogHistoryTimeline({ history }) {
  const { items, total, error, isLoading, hasMore, fetchNextPage } = history;

  if (error) {
    return <p className="tw-text-sm tw-text-status-negative">{error}</p>;
  }
  if (isLoading && !items.length) {
    return <GenericTableSkeleton numberOfRows={3} />;
  }
  if (!items.length) {
    return (
      <p className="tw-text-sm tw-text-text-dark-medium-importance">
        Aucun événement enregistré pour cet envoi. L’historique couvre les
        envois réalisés depuis la mise en place de ce suivi.
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <ol className="tw-list-none tw-m-0 tw-p-0 tw-ml-1">
        {items.map((event) => (
          <HistoryEvent key={event.id} event={event} />
        ))}
      </ol>
      {hasMore && (
        <div>
          <MainButton
            variant="secondary"
            text={`Voir plus (${total - items.length} restant${total - items.length > 1 ? 's' : ''})`}
            onClick={fetchNextPage}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}
